import api from './client';

// Subscriptions
export const fetchPlans = () => api.get('/subscription/plans').then(r => r.data);
export const fetchMySubscription = () => api.get('/subscription/me').then(r => r.data);
export const startCheckout = (plan, interval = 'monthly', promoCode = null, returnPath = '/subscribe/success') =>
  api.post('/subscription/checkout', { plan, interval, promo_code: promoCode, return_path: returnPath }).then(r => r.data);
export const startTrial = () => api.post('/subscription/start-trial').then(r => r.data);
export const verifyCheckout = (order_id, plan, interval) =>
  api.post('/subscription/verify', { order_id, plan, interval }).then(r => r.data);
export const cancelSubscription = () => api.post('/subscription/cancel').then(r => r.data);
export const previewPromo = (code, plan, interval) =>
  api.post('/subscription/promo/preview', { code, plan, interval }).then(r => r.data);

// Promos (admin)
export const adminListPromos = () => api.get('/admin/promos').then(r => r.data);
export const adminCreatePromo = (payload) => api.post('/admin/promos', payload).then(r => r.data);
export const adminDeletePromo = (code) => api.delete(`/admin/promos/${code}`).then(r => r.data);
export const adminTogglePromo = (code) => api.put(`/admin/promos/${code}/toggle`).then(r => r.data);

// Roku
export const rokuStatus = () => api.get('/roku/status').then(r => r.data);

// Affiliates (admin)
export const adminGetAffiliates = () => api.get('/admin/affiliates').then(r => r.data);
export const adminSetAffiliates = (cfg) => api.put('/admin/affiliates', cfg).then(r => r.data);
export const adminAffiliateClicks = () => api.get('/admin/affiliates/clicks').then(r => r.data);

// Public affiliate redirect URL builder (no auth)
const _BACKEND = process.env.REACT_APP_BACKEND_URL;
export const affiliateOutUrl = (href, provider = '', title = '') =>
  `${_BACKEND}/api/affiliate/out?href=${encodeURIComponent(href)}${provider ? `&provider=${encodeURIComponent(provider)}` : ''}${title ? `&title=${encodeURIComponent(title)}` : ''}`;

// Pay-Per-View
export const checkAccess = (showId) => api.get(`/shows/${showId}/access`).then(r => r.data);
export const ppvCheckout = (showId) => api.post(`/shows/${showId}/checkout`).then(r => r.data);
export const ppvVerify = (showId, order_id) => api.post(`/shows/${showId}/verify`, { order_id }).then(r => r.data);

// VIP codes
export const redeemVip = (code) => api.post('/vip/redeem', { code }).then(r => r.data);
export const adminListVip = () => api.get('/admin/vip-codes').then(r => r.data);
export const adminCreateVip = (payload) => api.post('/admin/vip-codes', payload).then(r => r.data);
export const adminDeleteVip = (code) => api.delete(`/admin/vip-codes/${code}`).then(r => r.data);
export const adminToggleVip = (code) => api.put(`/admin/vip-codes/${code}/toggle`).then(r => r.data);
export const uploadVideo = async (file, onProgress, outerSignal) => {
  const ctrl = new AbortController();
  outerSignal?.addEventListener('abort', () => ctrl.abort());
  const { signal } = ctrl;
  const fingerprint = `${file.name}:${file.size}:${file.lastModified}`;
  const sess = await api.post('/uploads/video/init', { filename: file.name, size: file.size, fingerprint }, { signal }).then(r => r.data);
  const { upload_id, chunk_size, total_chunks } = sess;
  const done = new Set(sess.received || []);
  const inflight = {};
  const report = () => {
    let loaded = 0;
    done.forEach((i) => { loaded += Math.min(chunk_size, file.size - i * chunk_size); });
    Object.values(inflight).forEach((b) => { loaded += b; });
    onProgress && onProgress(Math.min(100, Math.floor((loaded / file.size) * 100)), loaded);
  };
  report();
  const queue = [...Array(total_chunks).keys()].filter((i) => !done.has(i));
  const sendChunk = async (i) => {
    const blob = file.slice(i * chunk_size, Math.min(file.size, (i + 1) * chunk_size));
    for (let attempt = 0; ; attempt++) {
      try {
        await api.put(`/uploads/video/${upload_id}/chunk/${i}`, blob, {
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: 300000,
          signal,
          onUploadProgress: (e) => { inflight[i] = e.loaded; report(); },
        });
        delete inflight[i];
        done.add(i);
        report();
        return;
      } catch (err) {
        delete inflight[i];
        if (signal?.aborted || attempt >= 5) throw err;
        await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      }
    }
  };
  const worker = async () => { while (queue.length) await sendChunk(queue.shift()); };
  try {
    await Promise.all([worker(), worker(), worker()]);
  } catch (err) {
    ctrl.abort();
    throw err;
  }
  return api.post(`/uploads/video/${upload_id}/complete`, null, { signal }).then(r => r.data);
};

// Continue watching
export const fetchContinue = () => api.get('/continue').then(r => r.data);
export const pushContinue = (item) => api.post('/continue', item).then(r => r.data);
export const clearContinue = () => api.delete('/continue').then(r => r.data);

// Search history
export const fetchSearchHistory = () => api.get('/search-history').then(r => r.data);
export const pushSearchHistory = (query) => api.post('/search-history', { query }).then(r => r.data);
export const clearSearchHistory = () => api.delete('/search-history').then(r => r.data);

// Free Movies (Internet Archive public domain)
export const fetchFreeMovies = () => api.get('/free-movies', { timeout: 120000 }).then(r => r.data);

// Shows (filmmaker / originals)
export const fetchPublicShows = () => api.get('/shows').then(r => r.data);
export const fetchShow = (id) => api.get(`/shows/${id}`).then(r => r.data);
export const adminListShows = () => api.get('/admin/shows').then(r => r.data);
export const adminCreateShow = (payload) => api.post('/admin/shows', payload).then(r => r.data);
export const adminUpdateShow = (id, payload) => api.put(`/admin/shows/${id}`, payload).then(r => r.data);
export const adminDeleteShow = (id) => api.delete(`/admin/shows/${id}`).then(r => r.data);
export const adminTogglePublish = (id) => api.post(`/admin/shows/${id}/publish`).then(r => r.data);
